import {
  useDeleteMultiplesampleMouvement,
  useDuplicateMultiplesampleMouvement,
  useGetMultiplesampleMouvements,
  useUpdateMultiplesampleMouvementSequence,
  type MultiplesampleMouvement,
} from "@/api/multiplesample-mouvement.api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AddButton } from "@/components/ui/custom-buttons";
import { DataTable } from "@/components/ui/datatable";
import { toast } from "@/hooks/use-toast";
import { ColumnDef } from "@tanstack/react-table";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateOrEditExemplaireMouvement } from "./CreateOrEditExemplaireMouvement";

type ExemplaireMouvementTableProps = {
  exemplaireId?: string;
  disabled?: boolean;
};

type FormMode = "create" | "edit" | "view";

export const ExemplaireMouvementTable = ({
  exemplaireId,
  disabled = false,
}: ExemplaireMouvementTableProps) => {
  const { t } = useTranslation();

  const { data = [] } = useGetMultiplesampleMouvements(exemplaireId);

  const [rows, setRows] = useState<MultiplesampleMouvement[]>([]);
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | undefined>();

  const selectedMouvement = useRef<MultiplesampleMouvement>();

  const sequenceMutator = useUpdateMultiplesampleMouvementSequence();
  const deleter = useDeleteMultiplesampleMouvement();
  const duplicator = useDuplicateMultiplesampleMouvement();

  useEffect(() => {
    setRows(data);
  }, [data]);

  const openForm = (mode: FormMode, mouvement?: MultiplesampleMouvement) => {
    selectedMouvement.current = mouvement;
    setFormMode(mode);
  };

  const handleRowDragged = (newData: MultiplesampleMouvement[]) => {
    if (!exemplaireId) return;
    setRows(newData);
    sequenceMutator.mutate({
      newSequence: newData.map((d) => d.id),
      multiplesampleId: exemplaireId,
    });
  };

  const confirmDelete = () => {
    if (!deleteTargetId || !exemplaireId) return;
    deleter.mutate(
      { mouvementId: deleteTargetId, multiplesampleId: exemplaireId },
      {
        onSuccess: () => {
          toast({
            title: t("successDeleteExemplaireMouvement"),
            variant: "success",
          });
        },
        onError: () => {
          toast({
            title: t("errorDeleteExemplaireMouvement"),
            variant: "destructive",
          });
        },
        onSettled: () => setDeleteTargetId(undefined),
      },
    );
  };

  const handleDuplicate = useCallback(
    (mouvement: MultiplesampleMouvement) => {
      if (!exemplaireId) return;
      duplicator.mutate(
        { mouvementId: mouvement.id, multiplesampleId: exemplaireId },
        {
          onSuccess: () => {
            toast({
              title: t("successDuplicateExemplaireMouvement"),
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("errorDuplicateExemplaireMouvement"),
              variant: "destructive",
            });
          },
        },
      );
    },
    [duplicator, exemplaireId, t],
  );

  const datatableColumns = useMemo(
    (): ColumnDef<MultiplesampleMouvement>[] => [
      {
        size: 600,
        header: t("tab_exemplaire.mouvement.description"),
        accessorKey: "description",
      },
      {
        header: t("tab_exemplaire.mouvement.lieu"),
        accessorKey: "lieu",
      },
      {
        header: t("tab_exemplaire.mouvement.dateDepart"),
        accessorKey: "dateDepart",
      },
      {
        header: t("tab_exemplaire.mouvement.dateRetour"),
        accessorKey: "dateRetour",
      },
      createActionsColumnDef<MultiplesampleMouvement>(({ row }) => {
        const mouvement = row.original;
        const actions: TableRowAction[] = [
          {
            id: "edit",
            label: t(ACTION_I18N_KEYS.edit),
            icon: Pencil,
            disabled: disabled,
            onSelect: () => openForm("edit", mouvement),
          },
          {
            id: "duplicate",
            label: t(ACTION_I18N_KEYS.duplicate),
            icon: Copy,
            disabled: disabled,
            onSelect: () => handleDuplicate(mouvement),
          },
          {
            id: "delete",
            label: t(ACTION_I18N_KEYS.delete),
            icon: Trash2,
            destructive: true,
            disabled: disabled,
            onSelect: () => setDeleteTargetId(mouvement.id),
          },
        ];

        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            <TableRowActionsMenu actions={actions} />
          </div>
        );
      }),
    ],
    [t, disabled, handleDuplicate],
  );

  if (!exemplaireId) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <CreateOrEditExemplaireMouvement
        open={formMode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFormMode(null);
            selectedMouvement.current = undefined;
          }
        }}
        exemplaireId={exemplaireId}
        mouvement={
          formMode === "create" ? undefined : selectedMouvement.current
        }
        readOnly={formMode === "view"}
        disabled={disabled}
      />

      <AlertDialog
        open={deleteTargetId !== undefined}
        onOpenChange={(open) => !open && setDeleteTargetId(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmDeleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex justify-between items-baseline">
        <label className="text-sm font-medium text-gray-700">
          {t("exemplaire.mouvement.tableLabel")}
        </label>
        <AddButton
          type="button"
          onClick={() => openForm("create")}
          disabled={disabled}
        />
      </div>

      <DataTable
        enableDragRows
        onRowDragged={(d) => handleRowDragged(d.newData)}
        columns={datatableColumns}
        data={rows}
      />
    </div>
  );
};

export default ExemplaireMouvementTable;
